// src/pages/SecretaryReport.jsx
// "Project Form" report -- mirrors the club's official Project Form template.
// Saves to the backend, and exports the filled form as:
//   1. PDF  (jspdf)  -- template layout, dark gray + dark red header
//   2. Word (docx)   -- same layout, editable  ->  npm install docx
//
// LOGOS: put your two logo files in /public and adjust the paths below.
//   LOGO_LEFT_URL  = club lion logo   (shown top-left of the header)
//   LOGO_RIGHT_URL = district / theme logo (shown top-right of the header)
// If a logo file is missing the export still works (text-only header).

import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import {
  FaArrowLeft,
  FaPlus,
  FaTrash,
  FaSave,
  FaFilePdf,
  FaFileWord,
} from "react-icons/fa";
import { authFetch } from "../utils/api";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

// Council year shown on the web form and inside the exported files
const COUNCIL_YEAR = "2026/2027";
const DOC_COUNCIL_YEAR = "2026-2027";

// ---- Logo paths (files go in /public) -------------------------------------
const LOGO_LEFT_URL = "/leo.png"; // <-- your club logo (left side)
const LOGO_RIGHT_URL = "/district.png"; // <-- district/theme logo (right side)

function readUserFromStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem("user")) || {};
    return { name: "Unknown User", role: "Member", ...raw };
  } catch {
    return { name: "Unknown User", role: "Member" };
  }
}

const toJsonSafe = async (res) => {
  const t = await res.text();
  try {
    return JSON.parse(t);
  } catch {
    throw new Error(t.slice(0, 400));
  }
};

const emptyForm = {
  project_title: "",
  project_category: "",
  chairperson_name: "",
  secretary_name: "",
  treasurer_name: "",
  project_date: "",
  project_time: "",
  venue: "",

  is_lions_global_service_challenge: false,
  is_joint_project: false,
  joint_institutions: "",
  is_regional_zonal_project: false,
  regional_zonal_club_names: "",
  is_on_behalf_district_council: false,
  district_council_request_type: "",

  chief_guest: "",
  guest_of_honor: "",

  club_members_count: 0,
  visiting_leos_count: 0,
  club_advisor_present: false,
  visiting_lions_count: 0,
  district_president_present: false,
  lions_cabinet_officers_count: 0,
  region_director_present: false,
  resource_personnel_count: 0,
  zone_director_present: false,
  parents_count: 0,
  council_officers_count: 0,
  guests_count: 0,

  beneficiaries_count: 0,
  beneficiaries_description: "",
  service_hours: 0,

  project_overview: "",
  planning_description: "",
  implementation_description: "",
  conclusion_description: "",

  club_contribution: "",

  sdg_goals: [],
  sdg_targets: [],
  sustainability_description: "",
};

const FORM_PAGES = [
  { key: "general", label: "1 · General & Type" },
  { key: "participants", label: "2 · Invitees & Participants" },
  { key: "effectiveness", label: "3 · Effectiveness & Description" },
  { key: "sustainability", label: "4 · Contribution & Sustainability" },
];

// Slugifies a field label into a stable id so <label htmlFor> can pair with
// its input/textarea/select for accessibility.
const slugify = (s) =>
  String(s || "field")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

function Field({ label, span = 4, children }) {
  const isFormEl =
    React.isValidElement(children) &&
    typeof children.type === "string" &&
    ["input", "textarea", "select"].includes(children.type);
  const id = label && isFormEl ? `sr-${slugify(label)}` : undefined;
  const child = id ? React.cloneElement(children, { id: children.props.id || id }) : children;
  return (
    <div className={`field field-span-${span}`} style={{ marginBottom: 0 }}>
      {label && (
        <label className="field-label" htmlFor={id}>
          {label}
        </label>
      )}
      {child}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="panel" style={{ marginBottom: "var(--sp-5)" }}>
      <div className="section-head">
        <h2>{title}</h2>
      </div>
      <div className="form-grid">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13.5,
        color: "var(--text-1)",
        cursor: "pointer",
      }}
    >
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

export default function SecretaryReport() {
  const params = useParams();
  const projectId = useMemo(() => {
    const raw = params.projectId ?? params.id ?? null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [params]);

  const user = readUserFromStorage();
  const [form, setForm] = useState(emptyForm);
  const [activePage, setActivePage] = useState(FORM_PAGES[0].key);
  const [loading, setLoading] = useState(!!projectId);
  const [saving, setSaving] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  const [projectName, setProjectName] = useState("");

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${ABSOLUTE_HOST}/Backend/project_api.php?action=get_details&id=${projectId}`
        );
        const data = await toJsonSafe(res).catch(() => null);
        if (!cancelled && data?.success) {
          setProjectName(data.project?.name || `Project ${projectId}`);
        }
      } catch {
        /* non-critical */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await authFetch(
          `${ABSOLUTE_HOST}/Backend/project_secretary_report.php?action=get&project_id=${projectId}`
        );
        const data = await toJsonSafe(res);
        if (cancelled) return;
        if (data?.success && data.data) {
          setForm({ ...emptyForm, ...data.data });
        }
      } catch (e) {
        console.error("load secretary report error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handleSave = async () => {
    if (!projectId) {
      alert("Missing or invalid project ID in the URL.");
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch(
        `${ABSOLUTE_HOST}/Backend/project_secretary_report.php?action=save`,
        {
          method: "POST",
          body: JSON.stringify({ ...form, project_id: projectId }),
        }
      );
      const data = await toJsonSafe(res);
      if (!data?.success) throw new Error(data?.error || "Save failed");
      alert("Project form saved.");
    } catch (e) {
      console.error("save secretary report error:", e);
      alert(e?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const addSdgGoal = () =>
    setForm((f) => ({
      ...f,
      sdg_goals: [...(f.sdg_goals || []), { number: "", name: "" }],
    }));
  const updateSdgGoal = (i, key, value) =>
    setForm((f) => {
      const goals = [...(f.sdg_goals || [])];
      goals[i] = { ...goals[i], [key]: value };
      return { ...f, sdg_goals: goals };
    });
  const removeSdgGoal = (i) =>
    setForm((f) => ({
      ...f,
      sdg_goals: (f.sdg_goals || []).filter((_, idx) => idx !== i),
    }));

  const addSdgTarget = () =>
    setForm((f) => ({
      ...f,
      sdg_targets: [
        ...(f.sdg_targets || []),
        { sdgNumber: "", targetNumber: "", name: "" },
      ],
    }));
  const updateSdgTarget = (i, key, value) =>
    setForm((f) => {
      const targets = [...(f.sdg_targets || [])];
      targets[i] = { ...targets[i], [key]: value };
      return { ...f, sdg_targets: targets };
    });
  const removeSdgTarget = (i) =>
    setForm((f) => ({
      ...f,
      sdg_targets: (f.sdg_targets || []).filter((_, idx) => idx !== i),
    }));

  const safeFileName = () =>
    (form.project_title || projectName || "Project").replace(/[^a-z0-9\-_ ]/gi, "");

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      await renderProjectFormPdf(doc, form);
      doc.save(`${safeFileName()} - Project Form.pdf`);
    } catch (e) {
      console.error("PDF export error:", e);
      alert("Could not generate the PDF. Please try again.");
    } finally {
      setExportingPDF(false);
    }
  };

  const handleExportDocx = async () => {
    setExportingDocx(true);
    try {
      const blob = await buildProjectFormDocx(form);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFileName()} - Project Form.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      console.error("Word export error:", e);
      alert("Could not generate the Word file. Please try again.");
    } finally {
      setExportingDocx(false);
    }
  };

  return (
    <>
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 md:px-8 py-6 md:py-10">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="page-head">
            <div>
              <Link
                to={projectId ? `/project/${projectId}` : "/club-projects"}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: "var(--link)",
                  fontSize: 13,
                  textDecoration: "none",
                  marginBottom: 8,
                }}
              >
                <FaArrowLeft /> Back to project
              </Link>
              <h1>
                Project Form {COUNCIL_YEAR}
                {projectName ? ` — ${projectName}` : ""}
              </h1>
              <p className="page-desc">
                Fill in the project form. Save anytime; download the official PDF or Word file whenever you need it.
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button onClick={handleExportDocx} disabled={exportingDocx} className="btn btn-ghost">
                <FaFileWord /> {exportingDocx ? "Generating…" : "Download Word"}
              </button>
              <button onClick={handleExportPDF} disabled={exportingPDF} className="btn btn-ghost">
                <FaFilePdf /> {exportingPDF ? "Generating…" : "Download PDF"}
              </button>
              <button onClick={handleSave} disabled={saving || loading} className="btn btn-primary">
                <FaSave /> {saving ? "Saving…" : "Save Report"}
              </button>
            </div>
          </div>

          {!loading && (
            <div
              className="filter-row"
              style={{
                flexWrap: "wrap",
                marginBottom: "var(--sp-5)",
                borderBottom: "1px solid var(--border)",
                paddingBottom: "var(--sp-4)",
              }}
            >
              {FORM_PAGES.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setActivePage(p.key)}
                  className={`chip${activePage === p.key ? " active" : ""}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="empty-state">
              <div className="spinner lg" />
              <p>Loading report…</p>
            </div>
          ) : (
            <>
              {activePage === "general" && (
                <>
                  <Section title="General Information">
                    <Field label="Project Title" span={6}>
                      <input value={form.project_title} onChange={(e) => set("project_title")(e.target.value)} />
                    </Field>
                    <Field label="Project Category" span={6}>
                      <input value={form.project_category} onChange={(e) => set("project_category")(e.target.value)} />
                    </Field>
                    <Field label="Project Chairperson" span={4}>
                      <input value={form.chairperson_name} onChange={(e) => set("chairperson_name")(e.target.value)} />
                    </Field>
                    <Field label="Project Secretary" span={4}>
                      <input value={form.secretary_name} onChange={(e) => set("secretary_name")(e.target.value)} />
                    </Field>
                    <Field label="Project Treasurer" span={4}>
                      <input value={form.treasurer_name} onChange={(e) => set("treasurer_name")(e.target.value)} />
                    </Field>
                    <Field label="Date" span={4}>
                      <input type="date" value={form.project_date || ""} onChange={(e) => set("project_date")(e.target.value)} />
                    </Field>
                    <Field label="Time" span={4}>
                      <input placeholder="e.g. 4.30 PM" value={form.project_time} onChange={(e) => set("project_time")(e.target.value)} />
                    </Field>
                    <Field label="Venue" span={4}>
                      <input value={form.venue} onChange={(e) => set("venue")(e.target.value)} />
                    </Field>
                  </Section>

                  <Section title="Type of Project">
                    <Field span={6}>
                      <Toggle
                        label="Lions Global Service Challenge"
                        checked={form.is_lions_global_service_challenge}
                        onChange={set("is_lions_global_service_challenge")}
                      />
                    </Field>
                    <Field span={6}>
                      <Toggle label="Joint Project" checked={form.is_joint_project} onChange={set("is_joint_project")} />
                    </Field>
                    <Field label="Joint Club/District/Other Institutions" span={12}>
                      <input value={form.joint_institutions} onChange={(e) => set("joint_institutions")(e.target.value)} />
                    </Field>
                    <Field span={6}>
                      <Toggle
                        label="Regional/Zonal Project"
                        checked={form.is_regional_zonal_project}
                        onChange={set("is_regional_zonal_project")}
                      />
                    </Field>
                    <Field label="If Yes, club names" span={6}>
                      <input value={form.regional_zonal_club_names} onChange={(e) => set("regional_zonal_club_names")(e.target.value)} />
                    </Field>
                    <Field span={6}>
                      <Toggle
                        label="On Behalf of District Council 306C1"
                        checked={form.is_on_behalf_district_council}
                        onChange={set("is_on_behalf_district_council")}
                      />
                    </Field>
                    <Field label="Type of Request" span={6}>
                      <input value={form.district_council_request_type} onChange={(e) => set("district_council_request_type")(e.target.value)} />
                    </Field>
                  </Section>
                </>
              )}

              {activePage === "participants" && (
                <>
                  <Section title="Special Invitees Information">
                    <Field label="Chief Guest" span={6}>
                      <input value={form.chief_guest} onChange={(e) => set("chief_guest")(e.target.value)} />
                    </Field>
                    <Field label="Guest of Honor" span={6}>
                      <input value={form.guest_of_honor} onChange={(e) => set("guest_of_honor")(e.target.value)} />
                    </Field>
                  </Section>

                  <Section title="Participants Information">
                    <Field label="Club Members" span={6}>
                      <input type="number" min="0" value={form.club_members_count} onChange={(e) => set("club_members_count")(e.target.value)} />
                    </Field>
                    <Field label="Visiting Leos" span={6}>
                      <input type="number" min="0" value={form.visiting_leos_count} onChange={(e) => set("visiting_leos_count")(e.target.value)} />
                    </Field>

                    <Field span={6}>
                      <Toggle label="Club Advisor Present" checked={form.club_advisor_present} onChange={set("club_advisor_present")} />
                    </Field>
                    <Field label="Visiting Lions" span={6}>
                      <input type="number" min="0" value={form.visiting_lions_count} onChange={(e) => set("visiting_lions_count")(e.target.value)} />
                    </Field>

                    <Field span={6}>
                      <Toggle label="District President Present" checked={form.district_president_present} onChange={set("district_president_present")} />
                    </Field>
                    <Field label="Lions Cabinet Officers" span={6}>
                      <input type="number" min="0" value={form.lions_cabinet_officers_count} onChange={(e) => set("lions_cabinet_officers_count")(e.target.value)} />
                    </Field>

                    <Field span={6}>
                      <Toggle label="Region Director Present" checked={form.region_director_present} onChange={set("region_director_present")} />
                    </Field>
                    <Field label="Resource Personnel" span={6}>
                      <input type="number" min="0" value={form.resource_personnel_count} onChange={(e) => set("resource_personnel_count")(e.target.value)} />
                    </Field>

                    <Field span={6}>
                      <Toggle label="Zone Director Present" checked={form.zone_director_present} onChange={set("zone_director_present")} />
                    </Field>
                    <Field label="Parents" span={6}>
                      <input type="number" min="0" value={form.parents_count} onChange={(e) => set("parents_count")(e.target.value)} />
                    </Field>

                    <Field label="Council Officers" span={6}>
                      <input type="number" min="0" value={form.council_officers_count} onChange={(e) => set("council_officers_count")(e.target.value)} />
                    </Field>
                    <Field label="Guests" span={6}>
                      <input type="number" min="0" value={form.guests_count} onChange={(e) => set("guests_count")(e.target.value)} />
                    </Field>
                  </Section>
                </>
              )}

              {activePage === "effectiveness" && (
                <>
                  <Section title="Effectiveness of the Project">
                    <Field label="Number of Beneficiaries" span={4}>
                      <input type="number" min="0" value={form.beneficiaries_count} onChange={(e) => set("beneficiaries_count")(e.target.value)} />
                    </Field>
                    <Field label="Service Hours" span={4}>
                      <input type="number" min="0" step="0.5" value={form.service_hours} onChange={(e) => set("service_hours")(e.target.value)} />
                    </Field>
                    <Field label="Brief Description of Beneficiaries" span={12}>
                      <textarea rows={5} value={form.beneficiaries_description} onChange={(e) => set("beneficiaries_description")(e.target.value)} />
                    </Field>
                  </Section>

                  <Section title="Brief Description of the Project">
                    <Field label="Overview" span={6}>
                      <textarea rows={6} value={form.project_overview} onChange={(e) => set("project_overview")(e.target.value)} />
                    </Field>
                    <Field label="Planning" span={6}>
                      <textarea rows={6} value={form.planning_description} onChange={(e) => set("planning_description")(e.target.value)} />
                    </Field>
                    <Field label="Implementation" span={6}>
                      <textarea rows={6} value={form.implementation_description} onChange={(e) => set("implementation_description")(e.target.value)} />
                    </Field>
                    <Field label="Conclusion" span={6}>
                      <textarea rows={6} value={form.conclusion_description} onChange={(e) => set("conclusion_description")(e.target.value)} />
                    </Field>
                  </Section>
                </>
              )}

              {activePage === "sustainability" && (
                <>
                  <Section title="Contribution of the Club Towards the Project">
                    <Field span={12}>
                      <textarea rows={5} value={form.club_contribution} onChange={(e) => set("club_contribution")(e.target.value)} />
                    </Field>
                  </Section>

                  <Section title="Sustainability of the Project">
                    <Field span={6}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span className="field-label" style={{ marginBottom: 0 }}>Covered Sustainable Development Goals</span>
                        <button type="button" onClick={addSdgGoal} className="btn btn-primary btn-sm">
                          <FaPlus size={11} /> Add Goal
                        </button>
                      </div>
                      {(form.sdg_goals || []).map((g, i) => (
                        <div key={i} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                          <input
                            style={{ flex: "0 0 90px" }}
                            placeholder="Goal #"
                            aria-label={`SDG goal ${i + 1} number`}
                            value={g.number}
                            onChange={(e) => updateSdgGoal(i, "number", e.target.value)}
                          />
                          <input
                            style={{ flex: 1, minWidth: 120 }}
                            placeholder="Goal name"
                            aria-label={`SDG goal ${i + 1} name`}
                            value={g.name}
                            onChange={(e) => updateSdgGoal(i, "name", e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => removeSdgGoal(i)}
                            className="icon-btn"
                            aria-label={`Remove SDG goal ${i + 1}`}
                            title="Remove"
                            style={{ color: "var(--error)" }}
                          >
                            <FaTrash size={12} />
                          </button>
                        </div>
                      ))}
                    </Field>

                    <Field span={6}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span className="field-label" style={{ marginBottom: 0 }}>Covered Targets of the SDGs</span>
                        <button type="button" onClick={addSdgTarget} className="btn btn-primary btn-sm">
                          <FaPlus size={11} /> Add Target
                        </button>
                      </div>
                      {(form.sdg_targets || []).map((t, i) => (
                        <div key={i} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                          <input
                            style={{ flex: "0 0 70px" }}
                            placeholder="SDG #"
                            aria-label={`SDG target ${i + 1} SDG number`}
                            value={t.sdgNumber}
                            onChange={(e) => updateSdgTarget(i, "sdgNumber", e.target.value)}
                          />
                          <input
                            style={{ flex: "0 0 90px" }}
                            placeholder="Target #"
                            aria-label={`SDG target ${i + 1} target number`}
                            value={t.targetNumber}
                            onChange={(e) => updateSdgTarget(i, "targetNumber", e.target.value)}
                          />
                          <input
                            style={{ flex: 1, minWidth: 120 }}
                            placeholder="Target name"
                            aria-label={`SDG target ${i + 1} name`}
                            value={t.name}
                            onChange={(e) => updateSdgTarget(i, "name", e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => removeSdgTarget(i)}
                            className="icon-btn"
                            aria-label={`Remove SDG target ${i + 1}`}
                            title="Remove"
                            style={{ color: "var(--error)" }}
                          >
                            <FaTrash size={12} />
                          </button>
                        </div>
                      ))}
                    </Field>

                    <Field label="Brief description of long-term impact and sustainability" span={12}>
                      <textarea rows={6} value={form.sustainability_description} onChange={(e) => set("sustainability_description")(e.target.value)} />
                    </Field>
                  </Section>
                </>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "var(--sp-2)" }}>
                <button
                  type="button"
                  onClick={() => {
                    const idx = FORM_PAGES.findIndex((p) => p.key === activePage);
                    if (idx > 0) setActivePage(FORM_PAGES[idx - 1].key);
                  }}
                  disabled={activePage === FORM_PAGES[0].key}
                  className="btn btn-ghost"
                >
                  ← Previous
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const idx = FORM_PAGES.findIndex((p) => p.key === activePage);
                    if (idx < FORM_PAGES.length - 1) setActivePage(FORM_PAGES[idx + 1].key);
                  }}
                  disabled={activePage === FORM_PAGES[FORM_PAGES.length - 1].key}
                  className="btn btn-ghost"
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </>
  );
}

// ===========================================================================
// SHARED EXPORT HELPERS
// ===========================================================================

const DARK_GRAY = [42, 45, 51];
const DARK_RED = [139, 26, 26];
const DARK_GRAY_HEX = "2A2D33";
const DARK_RED_HEX = "8B1A1A";

const val = (v) => {
  const s = String(v ?? "").trim();
  return s.length ? s : " ";
};

async function loadImageAsDataUrl(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null; // logos are a nice-to-have; never block export on them
  }
}

async function loadImageAsArrayBuffer(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

// ===========================================================================
// PDF EXPORT (jspdf) -- matches the official template:
//   * dark gray + dark red brand strip on every page
//   * double page border
//   * council header with LEFT and RIGHT logos on every page
//   * bordered section tables with Yes/No checkboxes
// ===========================================================================

const LINE_H = 4.4; // manual line height used everywhere (mm)

function drawCheckbox(doc, x, yBaseline, checked, size = 3.2) {
  const top = yBaseline - size + 0.8;
  doc.setDrawColor(70, 70, 70);
  doc.setLineWidth(0.25);
  doc.rect(x, top, size, size);
  if (checked) {
    doc.setLineWidth(0.35);
    doc.line(x, top, x + size, top + size);
    doc.line(x + size, top, x, top + size);
  }
  doc.setLineWidth(0.2);
}

function drawYesNoInline(doc, x, yBaseline, isYes) {
  const size = 3.2;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(30, 30, 30);
  let cx = x;
  drawCheckbox(doc, cx, yBaseline, isYes === true, size);
  cx += size + 1.4;
  doc.text("Yes", cx, yBaseline);
  cx += doc.getTextWidth("Yes") + 5;
  drawCheckbox(doc, cx, yBaseline, isYes === false, size);
  cx += size + 1.4;
  doc.text("No", cx, yBaseline);
}

/** Draw an array of text lines manually with a fixed line height. */
function drawLines(doc, lines, x, firstBaseline) {
  let ty = firstBaseline;
  for (const line of lines) {
    doc.text(line, x, ty);
    ty += LINE_H;
  }
}

async function renderProjectFormPdf(doc, form) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const STRIP_H = 18;
  const FRAME_PAD = 7;
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  const bottomLimit = pageHeight - 14;
  let y = 0;

  const [logoLeft, logoRight] = await Promise.all([
    loadImageAsDataUrl(LOGO_LEFT_URL),
    loadImageAsDataUrl(LOGO_RIGHT_URL),
  ]);

  const addImageSafe = (dataUrl, x, top, w, h) => {
    if (!dataUrl) return false;
    try {
      doc.addImage(dataUrl, "PNG", x, top, w, h);
      return true;
    } catch {
      return false;
    }
  };

  // ---- brand strip + frame + council header, drawn on EVERY page ---------
  const drawPageChrome = () => {
    // strip: dark red base, dark gray body with diagonal cut
    doc.setFillColor(DARK_RED[0], DARK_RED[1], DARK_RED[2]);
    doc.rect(0, 0, pageWidth, STRIP_H, "F");
    const cutX = pageWidth * 0.62;
    doc.setFillColor(DARK_GRAY[0], DARK_GRAY[1], DARK_GRAY[2]);
    doc.rect(0, 0, cutX, STRIP_H, "F");
    doc.triangle(cutX, 0, cutX + 12, 0, cutX, STRIP_H, "F");

    let tx = 8;
    if (addImageSafe(logoLeft, 6, 2, 14, 14)) tx = 23;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text("LEO CLUB OF SLIIT", tx, STRIP_H / 2 - 0.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(210, 210, 210);
    doc.text("DISTRICT 306 D6, LIONS INTERNATIONAL", tx, STRIP_H / 2 + 4.5);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(255, 255, 255);
    doc.text("PROJECT FORM", pageWidth - 8, STRIP_H / 2 + 2, { align: "right" });

    // double page border
    const fTop = STRIP_H + 5;
    doc.setDrawColor(20, 20, 20);
    doc.setLineWidth(0.8);
    doc.rect(FRAME_PAD, fTop, pageWidth - FRAME_PAD * 2, pageHeight - fTop - 7);
    doc.setLineWidth(0.25);
    doc.rect(
      FRAME_PAD + 1.6,
      fTop + 1.6,
      pageWidth - (FRAME_PAD + 1.6) * 2,
      pageHeight - fTop - 7 - 3.2
    );
    doc.setLineWidth(0.2);

    // council header: LEFT logo + centered titles + RIGHT logo
    const headTop = fTop + 4;
    addImageSafe(logoLeft, margin - 1, headTop, 15, 15);
    addImageSafe(logoRight, pageWidth - margin - 14, headTop, 15, 15);

    let hy = headTop + 6;
    doc.setTextColor(15, 15, 15);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12.5);
    doc.text(`Leo District Council 306 D6 ${DOC_COUNCIL_YEAR}`, pageWidth / 2, hy, {
      align: "center",
    });
    hy += 6.5;
    doc.setFontSize(11.5);
    doc.text("Club Performance & Evaluation – Project Form", pageWidth / 2, hy, {
      align: "center",
    });
    hy += 2.5;
    doc.setDrawColor(40, 40, 40);
    doc.setLineWidth(0.4);
    doc.line(margin + 18, hy, pageWidth - margin - 18, hy);
    doc.setLineWidth(0.2);

    y = Math.max(hy, headTop + 15) + 8;
  };

  const pageBreak = () => {
    doc.addPage();
    drawPageChrome();
  };

  const ensureSpace = (needed) => {
    if (y + needed > bottomLimit) pageBreak();
  };

  // Set the font BEFORE measuring, so splitTextToSize is accurate.
  const splitNormal = (textValue, width, size = 9) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(size);
    return doc.splitTextToSize(val(textValue), width);
  };

  const sectionTitle = (title) => {
    ensureSpace(18); // keep the title attached to at least one row
    doc.setDrawColor(60, 60, 60);
    doc.setLineWidth(0.35);
    doc.rect(margin, y, contentWidth, 7);
    doc.setLineWidth(0.2);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(15, 15, 15);
    doc.text(title.toUpperCase(), margin + 2, y + 4.8);
    y += 7;
  };

  // Standard label | value row. Row height fits BOTH the label and the value.
  const kvRow = (label, value, labelWidthRatio = 0.38) => {
    const labelWidth = contentWidth * labelWidthRatio;
    const valueWidth = contentWidth - labelWidth;
    const labelLines = splitNormal(label, labelWidth - 4);
    const valueLines = splitNormal(value, valueWidth - 4);
    const n = Math.max(labelLines.length, valueLines.length, 1);
    const h = n * LINE_H + 3.4;
    ensureSpace(h);
    doc.setDrawColor(90);
    doc.rect(margin, y, labelWidth, h);
    doc.rect(margin + labelWidth, y, valueWidth, h);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(20, 20, 20);
    drawLines(doc, labelLines, margin + 2, y + 5);
    drawLines(doc, valueLines, margin + labelWidth + 2, y + 5);
    y += h;
  };

  // label | [x]Yes [ ]No row
  const kvRowYesNo = (label, isYes, labelWidthRatio = 0.38) => {
    const labelWidth = contentWidth * labelWidthRatio;
    const valueWidth = contentWidth - labelWidth;
    const labelLines = splitNormal(label, labelWidth - 4);
    const h = Math.max(labelLines.length * LINE_H + 3.4, 8);
    ensureSpace(h);
    doc.setDrawColor(90);
    doc.rect(margin, y, labelWidth, h);
    doc.rect(margin + labelWidth, y, valueWidth, h);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(20, 20, 20);
    drawLines(doc, labelLines, margin + 2, y + 5);
    drawYesNoInline(doc, margin + labelWidth + 2, y + 5.2, isYes);
    y += h;
  };

  // label | value || label | value (participants table)
  const twoUpRow = (leftLabel, leftValue, rightLabel, rightValue) => {
    const half = contentWidth / 2;
    const labelW = half * 0.52;
    const lLab = splitNormal(leftLabel, labelW - 4);
    const rLab = splitNormal(rightLabel, labelW - 4);
    const lVal = splitNormal(leftValue, half - labelW - 4);
    const rVal = splitNormal(rightValue, half - labelW - 4);
    const n = Math.max(lLab.length, rLab.length, lVal.length, rVal.length, 1);
    const h = Math.max(n * LINE_H + 3.4, 8);
    ensureSpace(h);
    doc.setDrawColor(90);
    doc.rect(margin, y, labelW, h);
    doc.rect(margin + labelW, y, half - labelW, h);
    doc.rect(margin + half, y, labelW, h);
    doc.rect(margin + half + labelW, y, half - labelW, h);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(20, 20, 20);
    drawLines(doc, lLab, margin + 2, y + 5);
    drawLines(doc, lVal, margin + labelW + 2, y + 5);
    drawLines(doc, rLab, margin + half + 2, y + 5);
    drawLines(doc, rVal, margin + half + labelW + 2, y + 5);
    y += h;
  };

  // like twoUpRow, but the left value cell is a Yes/No checkbox pair
  const twoUpRowYesNoLeft = (leftLabel, leftIsYes, rightLabel, rightValue) => {
    const half = contentWidth / 2;
    const labelW = half * 0.52;
    const lLab = splitNormal(leftLabel, labelW - 4);
    const rLab = splitNormal(rightLabel, labelW - 4);
    const rVal = splitNormal(rightValue, half - labelW - 4);
    const n = Math.max(lLab.length, rLab.length, rVal.length, 1);
    const h = Math.max(n * LINE_H + 3.4, 8);
    ensureSpace(h);
    doc.setDrawColor(90);
    doc.rect(margin, y, labelW, h);
    doc.rect(margin + labelW, y, half - labelW, h);
    doc.rect(margin + half, y, labelW, h);
    doc.rect(margin + half + labelW, y, half - labelW, h);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(20, 20, 20);
    drawLines(doc, lLab, margin + 2, y + 5);
    drawLines(doc, rLab, margin + half + 2, y + 5);
    drawYesNoInline(doc, margin + labelW + 2, y + 5.2, leftIsYes);
    drawLines(doc, rVal, margin + half + labelW + 2, y + 5);
    y += h;
  };

  // label | LONG value -- splits across pages, redrawing borders per page
  const kvRowLong = (label, value, labelWidthRatio = 0.38) => {
    const labelWidth = contentWidth * labelWidthRatio;
    const valueWidth = contentWidth - labelWidth;
    const labelLines = splitNormal(label, labelWidth - 4);
    const valueLines = splitNormal(value, valueWidth - 4);
    let i = 0;
    let first = true;
    while (first || i < valueLines.length) {
      let avail = bottomLimit - y;
      const neededFirst = (Math.max(labelLines.length, 1) * LINE_H) + 3.4;
      if (avail < Math.max(20, first ? neededFirst : 0)) {
        pageBreak();
        avail = bottomLimit - y;
      }
      const maxLines = Math.max(1, Math.floor((avail - 3.4) / LINE_H));
      const chunk = valueLines.slice(i, i + maxLines);
      const rows = first
        ? Math.max(labelLines.length, chunk.length, 1)
        : Math.max(chunk.length, 1);
      const h = rows * LINE_H + 3.4;
      doc.setDrawColor(90);
      doc.rect(margin, y, labelWidth, h);
      doc.rect(margin + labelWidth, y, valueWidth, h);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(20, 20, 20);
      if (first) drawLines(doc, labelLines, margin + 2, y + 5);
      drawLines(doc, chunk, margin + labelWidth + 2, y + 5);
      y += h;
      i += chunk.length;
      first = false;
    }
  };

  // full-width bordered paragraph box (bold heading + body), page-safe
  const boxedParagraph = (heading, textValue) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const bodyLines = doc.splitTextToSize(val(textValue), contentWidth - 6);
    const all = [];
    if (heading) all.push({ bold: true, t: heading });
    for (const t of bodyLines) all.push({ bold: false, t });
    let i = 0;
    while (i < all.length) {
      let avail = bottomLimit - y;
      if (avail < 16) {
        pageBreak();
        avail = bottomLimit - y;
      }
      const maxLines = Math.max(1, Math.floor((avail - 4.5) / LINE_H));
      const chunk = all.slice(i, i + maxLines);
      const h = chunk.length * LINE_H + 4.5;
      doc.setDrawColor(90);
      doc.rect(margin, y, contentWidth, h);
      let ty = y + 5;
      for (const line of chunk) {
        doc.setFont("helvetica", line.bold ? "bold" : "normal");
        doc.setFontSize(line.bold ? 10 : 9.5);
        doc.setTextColor(20, 20, 20);
        doc.text(line.t, margin + 3, ty);
        ty += LINE_H;
      }
      y += h;
      i += chunk.length;
    }
  };

  // ======================= DOCUMENT CONTENT ==============================
  drawPageChrome();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(15, 15, 15);
  doc.text("Project Report", margin + 2, y);
  y += 5;

  sectionTitle("General Information");
  kvRow("Project Title", form.project_title);
  kvRow("Project Category", form.project_category);
  kvRow("Project Chairperson", form.chairperson_name);
  kvRow("Project Secretary", form.secretary_name);
  kvRow("Project Treasurer", form.treasurer_name);
  kvRow("Date", form.project_date);
  kvRow("Time", form.project_time);
  kvRow("Venue", form.venue);

  y += 4;
  sectionTitle("Type of Project");
  kvRowYesNo("Lions Global Service Challenge", form.is_lions_global_service_challenge);
  kvRowYesNo("Joint Project", form.is_joint_project);
  kvRow("Joint Club/District/Other Institutions (State the name(s))", form.joint_institutions);
  kvRowYesNo("Regional/Zonal Project", form.is_regional_zonal_project);
  kvRow("If Yes, State the club names", form.regional_zonal_club_names);
  kvRowYesNo("On Behalf of District Council 306C1", form.is_on_behalf_district_council);
  kvRow("Type of Request", form.district_council_request_type);

  y += 4;
  sectionTitle("Special Invitees Information");
  kvRow("Chief Guest", form.chief_guest);
  kvRow("Guest of Honor", form.guest_of_honor);

  y += 4;
  sectionTitle("Participants Information");
  twoUpRow("Club Members", form.club_members_count, "Visiting Leos", form.visiting_leos_count);
  twoUpRowYesNoLeft("Club Advisor", form.club_advisor_present, "Visiting Lions", form.visiting_lions_count);
  twoUpRowYesNoLeft("District President", form.district_president_present, "Lions Cabinet Officers", form.lions_cabinet_officers_count);
  twoUpRowYesNoLeft("Region Director", form.region_director_present, "Resource Personnel", form.resource_personnel_count);
  twoUpRowYesNoLeft("Zone Director", form.zone_director_present, "Parents", form.parents_count);
  twoUpRow("Council Officers", form.council_officers_count, "Guests", form.guests_count);

  y += 4;
  sectionTitle("Effectiveness of the Project");
  kvRow("Number of Beneficiaries", form.beneficiaries_count);
  kvRowLong("Brief Description of Beneficiaries", form.beneficiaries_description);
  kvRow("Service Hours", form.service_hours ? `${form.service_hours} Hours` : "");

  // Description starts on a fresh page, like the template
  pageBreak();
  sectionTitle("Brief Description of the Project");
  boxedParagraph("", form.project_overview);
  boxedParagraph("Planning", form.planning_description);
  boxedParagraph("Implementation", form.implementation_description);
  boxedParagraph("Conclusion", form.conclusion_description);

  y += 4;
  sectionTitle("Contribution of the Club Towards the Project");
  boxedParagraph("", form.club_contribution);

  y += 4;
  sectionTitle("Sustainability of the Project");
  const goals = (form.sdg_goals || []).filter((g) => g.number || g.name);
  kvRowLong(
    "Covered Sustainable Development Goals (Goal number and the name only)",
    goals.length ? goals.map((g) => `SDG ${g.number} – ${g.name}`).join("\n") : " ",
    0.42
  );
  const targets = (form.sdg_targets || []).filter((t) => t.sdgNumber || t.name);
  kvRowLong(
    "Covered Targets of the Sustainable Development Goals (SDG number, target number and the name only)",
    targets.length
      ? targets.map((t) => `SDG ${t.sdgNumber}, Target ${t.targetNumber} – ${t.name}`).join("\n")
      : " ",
    0.42
  );
  kvRowLong(
    "Brief description of long-term impact and sustainability of the project",
    form.sustainability_description,
    0.42
  );
}

// ===========================================================================
// WORD (.docx) EXPORT  --  npm install docx
// Same structure as the template: brand strip + council header with two
// logos repeated on every page (Word page header), bordered section tables.
// ===========================================================================

async function buildProjectFormDocx(form) {
  const docx = await import("docx");
  const {
    Document,
    Packer,
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    AlignmentType,
    Header,
    ImageRun,
    BorderStyle,
    VerticalAlign,
  } = docx;

  const [logoLeftBuf, logoRightBuf] = await Promise.all([
    loadImageAsArrayBuffer(LOGO_LEFT_URL),
    loadImageAsArrayBuffer(LOGO_RIGHT_URL),
  ]);

  const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
  const noBorders = {
    top: NO_BORDER,
    bottom: NO_BORDER,
    left: NO_BORDER,
    right: NO_BORDER,
    insideHorizontal: NO_BORDER,
    insideVertical: NO_BORDER,
  };
  const cellMargins = { top: 60, bottom: 60, left: 100, right: 100 };

  const logoRun = (buf, size = 42) => {
    if (!buf) return null;
    try {
      return new ImageRun({
        type: "png",
        data: buf,
        transformation: { width: size, height: size },
      });
    } catch {
      return null;
    }
  };

  const text = (t, opts = {}) =>
    new TextRun({ text: String(t ?? ""), size: 19, ...opts });

  const para = (children, opts = {}) =>
    new Paragraph({ children: Array.isArray(children) ? children : [children], ...opts });

  const yesNo = (isYes) => (isYes ? "☒ Yes    ☐ No" : "☐ Yes    ☒ No");

  // ---- page header (repeats on every page): brand strip + council titles --
  const leftRuns = [];
  const stripLogo = logoRun(logoLeftBuf, 38);
  if (stripLogo) leftRuns.push(stripLogo, text("   "));
  leftRuns.push(text("LEO CLUB OF SLIIT", { bold: true, color: "FFFFFF", size: 26 }));

  const brandStrip = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: noBorders,
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: 65, type: WidthType.PERCENTAGE },
            shading: { fill: DARK_GRAY_HEX },
            margins: cellMargins,
            verticalAlign: VerticalAlign.CENTER,
            borders: noBorders,
            children: [
              para(leftRuns),
              para(
                text("DISTRICT 306 D6, LIONS INTERNATIONAL", {
                  color: "D2D2D2",
                  size: 14,
                })
              ),
            ],
          }),
          new TableCell({
            width: { size: 35, type: WidthType.PERCENTAGE },
            shading: { fill: DARK_RED_HEX },
            margins: cellMargins,
            verticalAlign: VerticalAlign.CENTER,
            borders: noBorders,
            children: [
              para(text("PROJECT FORM", { bold: true, color: "FFFFFF", size: 30 }), {
                alignment: AlignmentType.RIGHT,
              }),
            ],
          }),
        ],
      }),
    ],
  });

  const leftHeadLogo = logoRun(logoLeftBuf, 48);
  const rightHeadLogo = logoRun(logoRightBuf, 48);

  const councilHeader = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: noBorders,
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: 15, type: WidthType.PERCENTAGE },
            borders: noBorders,
            verticalAlign: VerticalAlign.CENTER,
            children: [leftHeadLogo ? para(leftHeadLogo) : para(text(" "))],
          }),
          new TableCell({
            width: { size: 70, type: WidthType.PERCENTAGE },
            borders: noBorders,
            verticalAlign: VerticalAlign.CENTER,
            children: [
              para(
                text(`Leo District Council 306 D6 ${DOC_COUNCIL_YEAR}`, {
                  bold: true,
                  size: 26,
                }),
                { alignment: AlignmentType.CENTER }
              ),
              para(
                text("Club Performance & Evaluation – Project Form", {
                  bold: true,
                  size: 24,
                }),
                {
                  alignment: AlignmentType.CENTER,
                  border: {
                    bottom: { style: BorderStyle.SINGLE, size: 8, color: "222222" },
                  },
                }
              ),
            ],
          }),
          new TableCell({
            width: { size: 15, type: WidthType.PERCENTAGE },
            borders: noBorders,
            verticalAlign: VerticalAlign.CENTER,
            children: [
              rightHeadLogo
                ? para(rightHeadLogo, { alignment: AlignmentType.RIGHT })
                : para(text(" ")),
            ],
          }),
        ],
      }),
    ],
  });

  const pageHeader = new Header({
    children: [
      brandStrip,
      para(text(" ", { size: 8 })),
      councilHeader,
      para(text(" ", { size: 8 })),
    ],
  });

  // ---- body helpers -------------------------------------------------------
  const sectionTitleTable = (title) =>
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              margins: cellMargins,
              shading: { fill: "F2F2F2" },
              children: [para(text(title.toUpperCase(), { bold: true, size: 20 }))],
            }),
          ],
        }),
      ],
    });

  const cell = (children, widthPct) =>
    new TableCell({
      width: { size: widthPct, type: WidthType.PERCENTAGE },
      margins: cellMargins,
      verticalAlign: VerticalAlign.CENTER,
      children,
    });

  const multiline = (value) =>
    String(value ?? "")
      .split("\n")
      .map((line) => para(text(line)));

  const kvTableRow = (label, value) =>
    new TableRow({
      children: [cell([para(text(label))], 38), cell(multiline(value), 62)],
    });

  const kvYesNoRow = (label, isYes) =>
    new TableRow({
      children: [cell([para(text(label))], 38), cell([para(text(yesNo(isYes)))], 62)],
    });

  const twoUpTableRow = (lLabel, lValue, rLabel, rValue) =>
    new TableRow({
      children: [
        cell([para(text(lLabel))], 26),
        cell([para(text(lValue ?? ""))], 24),
        cell([para(text(rLabel))], 26),
        cell([para(text(rValue ?? ""))], 24),
      ],
    });

  const twoUpYesNoRow = (lLabel, lIsYes, rLabel, rValue) =>
    new TableRow({
      children: [
        cell([para(text(lLabel))], 26),
        cell([para(text(yesNo(lIsYes)))], 24),
        cell([para(text(rLabel))], 26),
        cell([para(text(rValue ?? ""))], 24),
      ],
    });

  const rowsTable = (rows) =>
    new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows });

  const paragraphCellContent = (heading, body) => {
    const out = [];
    if (heading) out.push(para(text(heading, { bold: true, size: 21 })));
    for (const c of String(body ?? "").split("\n")) out.push(para(text(c)));
    out.push(para(text(" ", { size: 10 })));
    return out;
  };

  const boxedParagraphTable = (blocks) =>
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              margins: { top: 100, bottom: 100, left: 120, right: 120 },
              children: blocks,
            }),
          ],
        }),
      ],
    });

  const spacer = () => para(text(" ", { size: 10 }));

  const goals = (form.sdg_goals || []).filter((g) => g.number || g.name);
  const targets = (form.sdg_targets || []).filter((t) => t.sdgNumber || t.name);

  const children = [
    para(text("Project Report", { bold: true, size: 22 })),
    spacer(),

    sectionTitleTable("General Information"),
    rowsTable([
      kvTableRow("Project Title", form.project_title),
      kvTableRow("Project Category", form.project_category),
      kvTableRow("Project Chairperson", form.chairperson_name),
      kvTableRow("Project Secretary", form.secretary_name),
      kvTableRow("Project Treasurer", form.treasurer_name),
      kvTableRow("Date", form.project_date),
      kvTableRow("Time", form.project_time),
      kvTableRow("Venue", form.venue),
    ]),
    spacer(),

    sectionTitleTable("Type of Project"),
    rowsTable([
      kvYesNoRow("Lions Global Service Challenge", form.is_lions_global_service_challenge),
      kvYesNoRow("Joint Project", form.is_joint_project),
      kvTableRow("Joint Club/District/Other Institutions (State the name(s))", form.joint_institutions),
      kvYesNoRow("Regional/Zonal Project", form.is_regional_zonal_project),
      kvTableRow("If Yes, State the club names", form.regional_zonal_club_names),
      kvYesNoRow("On Behalf of District Council 306C1", form.is_on_behalf_district_council),
      kvTableRow("Type of Request", form.district_council_request_type),
    ]),
    spacer(),

    sectionTitleTable("Special Invitees Information"),
    rowsTable([
      kvTableRow("Chief Guest", form.chief_guest),
      kvTableRow("Guest of Honor", form.guest_of_honor),
    ]),
    spacer(),

    sectionTitleTable("Participants Information"),
    rowsTable([
      twoUpTableRow("Club Members", form.club_members_count, "Visiting Leos", form.visiting_leos_count),
      twoUpYesNoRow("Club Advisor", form.club_advisor_present, "Visiting Lions", form.visiting_lions_count),
      twoUpYesNoRow("District President", form.district_president_present, "Lions Cabinet Officers", form.lions_cabinet_officers_count),
      twoUpYesNoRow("Region Director", form.region_director_present, "Resource Personnel", form.resource_personnel_count),
      twoUpYesNoRow("Zone Director", form.zone_director_present, "Parents", form.parents_count),
      twoUpTableRow("Council Officers", form.council_officers_count, "Guests", form.guests_count),
    ]),
    spacer(),

    sectionTitleTable("Effectiveness of the Project"),
    rowsTable([
      kvTableRow("Number of Beneficiaries", form.beneficiaries_count),
      kvTableRow("Brief Description of Beneficiaries", form.beneficiaries_description),
      kvTableRow("Service Hours", form.service_hours ? `${form.service_hours} Hours` : ""),
    ]),
    spacer(),

    sectionTitleTable("Brief Description of the Project"),
    boxedParagraphTable([
      ...paragraphCellContent("", form.project_overview),
      ...paragraphCellContent("Planning", form.planning_description),
      ...paragraphCellContent("Implementation", form.implementation_description),
      ...paragraphCellContent("Conclusion", form.conclusion_description),
    ]),
    spacer(),

    sectionTitleTable("Contribution of the Club Towards the Project"),
    boxedParagraphTable(paragraphCellContent("", form.club_contribution)),
    spacer(),

    sectionTitleTable("Sustainability of the Project"),
    rowsTable([
      kvTableRow(
        "Covered Sustainable Development Goals (Goal number and the name only)",
        goals.length ? goals.map((g) => `SDG ${g.number} – ${g.name}`).join("\n") : ""
      ),
      kvTableRow(
        "Covered Targets of the Sustainable Development Goals (SDG number, target number and the name only)",
        targets.length
          ? targets.map((t) => `SDG ${t.sdgNumber}, Target ${t.targetNumber} – ${t.name}`).join("\n")
          : ""
      ),
      kvTableRow(
        "Brief description of long-term impact and sustainability of the project",
        form.sustainability_description
      ),
    ]),
  ];

  const wordDoc = new Document({
    styles: {
      default: {
        document: { run: { font: "Times New Roman", size: 19 } },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: { top: 500, bottom: 700, left: 850, right: 850 },
          },
        },
        headers: { default: pageHeader },
        children,
      },
    ],
  });

  return Packer.toBlob(wordDoc);
}